import { useState } from "react";
import { useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";

import { incrementDataRevision } from "app/store/tools/revisionSlice";
import { createRequest, createHeaders, throwResErrors } from "app/services/requestUtil";
import { showSnackbar } from "app/main/utils/snackbarUtil";

/**
 * ConfirmActionDialog - A dialog component for confirming actions with API calls
 *
 * @param {Object} props
 * @param {Object} props.dataIds - Contains:
 *   - title: Dialog title
 *   - message: Confirmation message
 *   - confirmLabel: Label for confirm button
 *   - url: API endpoint URL
 *   - listenEvent: Event name to dispatch after success (optional)
 *   - method: HTTP method (PUT, POST, DELETE) - defaults to PUT
 *   - body: Request body/payload (optional) - defaults to {}
 *   - successMessage: Custom success message (optional)
 *   - errorMessage: Custom error message (optional)
 * @param {Function} props.onDone - Callback when action is completed successfully
 * @param {Function} props.onClose - Callback to close the dialog
 */
function ConfirmActionDialog({ dataIds, onDone, onClose }) {
	const dispatch = useDispatch();
	const snackbar = useSnackbar();
	const [processing, setProcessing] = useState(false);

	const {
		title,
		message,
		confirmLabel,
		url,
		listenEvent,
		method = "PUT",
		body = {},
		successMessage = "Action completed successfully",
		errorMessage,
	} = dataIds || {};

	const handleConfirm = async () => {
		if (!url) {
			showSnackbar(snackbar, "No URL specified for action", "error");
			return;
		}

		setProcessing(true);
		try {
			const request = createRequest();
			const config = { headers: createHeaders() };

			// Prepare the request body - use provided body or default to empty object
			const requestBody = body || {};

			if (method === "PUT") {
				await request
					.put(url, requestBody, config)
					.then((res) => res.data)
					.catch(throwResErrors);
			} else if (method === "POST") {
				await request
					.post(url, requestBody, config)
					.then((res) => res.data)
					.catch(throwResErrors);
			} else if (method === "DELETE") {
				// DELETE typically doesn't have a body, but some APIs support it
				await request
					.delete(url, { ...config, data: requestBody })
					.then((res) => res.data)
					.catch(throwResErrors);
			}

			showSnackbar(snackbar, successMessage, "success");

			if (listenEvent) {
				dispatch(incrementDataRevision({ event: listenEvent }));
			}

			if (onDone) onDone();
		} catch (error) {
			const defaultErrorMessage = errorMessage || "Something went wrong, please try again later";
			const finalErrorMessage = error?.errors?.[0]?.message || error?.message || defaultErrorMessage;
			showSnackbar(snackbar, finalErrorMessage, "error");
		} finally {
			setProcessing(false);
		}
	};

	const handleCancel = () => {
		if (onClose) onClose();
	};

	return (
		<div className="flex flex-col p-20">
			{title && <Typography className="text-18 font-semibold mb-10">{title}</Typography>}
			{message && <Typography className="text-14 text-grey-700 mb-20 whitespace-pre-line">{message}</Typography>}
			<div className="flex flex-row justify-end space-x-10 mt-10">
				<Button variant="outlined" onClick={handleCancel} disabled={processing} className="min-w-100">
					Cancel
				</Button>
				<Button
					variant="contained"
					color="secondary"
					onClick={handleConfirm}
					disabled={processing}
					className="min-w-100"
				>
					{processing ? <CircularProgress size={20} color="inherit" /> : confirmLabel || "Confirm"}
				</Button>
			</div>
		</div>
	);
}

export default ConfirmActionDialog;
